/**
 * Reader for the browse shell inline (server/lib/browse-inline.ts): the category, first
 * page of cards and category tree the server embedded in the HTML of a `/cards*` page, so
 * the first paint doesn't wait on two serial API hops after the JS boots.
 *
 * Read once, from the DOM, on first use; the block is removed after parsing. Consumers seed
 * React Query with `initialData` + `initialDataUpdatedAt: loadedAt`, so the normal
 * staleness rules apply from the server's render time — a later client-side visit to the
 * same category refetches in the background exactly as it would without the inline.
 */
import type { BrowseInline } from "@server/lib/browse-inline";

export type { BrowseInline };

const SCRIPT_ID = "browse-inline";

let parsed: BrowseInline | null | undefined;

function read(): BrowseInline | null {
  if (parsed !== undefined) return parsed;
  parsed = null;
  if (typeof document === "undefined") return null;
  const el = document.getElementById(SCRIPT_ID);
  if (!el) return null;
  try {
    const value = JSON.parse(el.textContent ?? "") as BrowseInline;
    if (value && typeof value.path === "string" && Array.isArray(value.tree) && value.cards)
      parsed = value;
  } catch {
    parsed = null;
  }
  el.remove();
  return parsed;
}

/** The inline for this category path (the server's `full_path`, "" at the root), if the page carries one. */
export function inlineBrowseFor(path: string): BrowseInline | null {
  const v = read();
  return v && v.path === path ? v : null;
}
